import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { colors, fontSize } from "style";
import { Col, Row } from "components/Containers";
import { H5, Paragraph } from "components/Text";
import { ImageLoader, TextLoader } from "components/Loader";
import Card from "./index";

/**
 * CardMediaRow
 *
 * @param {Node}     media        Shown on the left, such as a thumbnail
 * @param {Node}     title        Shown at the top
 * @param {Node}     meta         Shown after the title, on its first line
 * @param {String}   description  Shown under the title
 * @param {Node}     actions      Shown last on the title's first line, such as an advanced menu
 * @param {Boolean}  loading      Show placeholders in the same layout
 */
const CardMediaRow = ({ media, title, meta, description, actions, loading, ...props }) => (
  <Card xsmallPadding noMargin {...props}>
    <Row align="flex-start" gap="2rem" noWrap>
      <div css={styles.media}>{loading ? <ImageLoader width="100%" height="9rem" radius="0.6rem" /> : media}</div>
      <Col gap="0.8rem" css={styles.body}>
        <Row align="baseline" gap="1.5rem" noWrap>
          {loading ? <TextLoader width="40%" /> : <H5 css={styles.title}>{title}</H5>}
          {!loading && meta && <span css={styles.meta}>{meta}</span>}
          {!loading && actions && (
            <Row gap="0.5rem" noWrap css={styles.actions}>
              {actions}
            </Row>
          )}
        </Row>
        {loading ? <Paragraph isLoading /> : description && <Paragraph data={description} css={styles.description} />}
      </Col>
    </Row>
  </Card>
);

const styles = {
  media: css`
    width: 16rem;
    flex-shrink: 0;
  `,
  body: css`
    flex: 1;
    min-width: 0;
  `,
  title: css`
    flex: 1;
    min-width: 0;
    margin: 0;
    line-height: 1.5;
  `,
  meta: css`
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyLight1};
    white-space: nowrap;
  `,
  description: css`
    font-size: ${fontSize.small};
    line-height: 1.3;
    color: ${colors.grayAnatomyBase};
    margin: 0;
  `,
  actions: css`
    flex-shrink: 0;
  `,
};

CardMediaRow.propTypes = {
  media: PropTypes.node,
  title: PropTypes.node,
  meta: PropTypes.node,
  description: PropTypes.string,
  actions: PropTypes.node,
  loading: PropTypes.bool,
};

export default CardMediaRow;
